import React, { useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import UserService from "services/user.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Select from "react-select";
import Details from "./Details";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  reports: {
    data: null,
    firstLoad: true,
  },
};

const Reports = () => {
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  // Initialize with cached data if available to prevent "No data found" flash
  const [data, setData] = useState(() => {
    const cache = tabCache.reports;
    return cache?.data?.data || [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    const cache = tabCache.reports;
    // Only show loading if no cache exists
    return cache?.firstLoad && !cache?.data;
  });
  const [reportType, setReportType] = useState("");
  const [searchText, setSearchText] = useState("");
  const [viewOpen, setViewOpen] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Initialize pagination with cached data if available
  const [totalPages, setTotalPages] = useState(() => {
    const cache = tabCache.reports;
    if (cache?.data?.totalCount) {
      return Math.ceil(cache.data.totalCount / 20);
    }
    return 1;
  });
  const [totalCount, setTotalCount] = useState(() => {
    const cache = tabCache.reports;
    return cache?.data?.totalCount || 0;
  });

  const getUser = async (showLoader = true) => {
    if (showLoader) {
    setIsLoading(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          projection: {},
          filters: {
            report_type: reportType === "" ? [] : [reportType], // post, account
          },
          sort: {},
        },
        searchtext: searchText,
      };
      const response = await UserService.getReports(body);
      const responseData = response.data.data;
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache.reports.data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache.reports.firstLoad) {
        tabCache.reports.firstLoad = false;
      }
      
      setData(responseData);
      setTotalCount(responseTotalCount);
      setTotalPages(Math.ceil(responseTotalCount / pageSize));
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to fetch reports");
    } finally {
      if (showLoader) {
      setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Check cache first
    const cache = tabCache.reports;
    
    // If cached data exists, show it instantly
    if (cache.data) {
      setData(cache.data.data || []);
      setTotalCount(cache.data.totalCount || 0);
      setTotalPages(Math.ceil((cache.data.totalCount || 0) / pageSize));
    }
    
    // Always fetch fresh data in background
    // Show loader only on firstLoad (when no cache exists yet)
    const shouldShowLoader = cache.firstLoad && !cache.data;
    getUser(shouldShowLoader);
  }, [currentPage, pageSize, searchText, reportType]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Time</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="text-sm font-bold text-white">
            {info.getValue()
              ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
              : "-"}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("message", {
      id: "message",
      header: () => (
        <p className="text-sm font-bold uppercase text-white text-left">Message</p>
      ),
      cell: (info) => (
        <p 
          className="text-sm text-white break-words whitespace-normal leading-relaxed text-left line-clamp-2"
          title={info.getValue()}
        >
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("reporter", {
      id: "reporter",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Reported by</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()?.name ? info.getValue()?.name : "--"}
        </p>
      ),
    }),
    columnHelper.accessor("account", {
      id: "account",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Report For</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue()?.name ? info.getValue()?.name : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("report_type", {
      id: "report_type",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Report Type</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("action", {
      id: "action",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Action</p>
      ),
      cell: (info) => {
        return (
          <div
            className="ite ms-center flex w-fit cursor-pointer rounded-md bg-primary-gradient p-1.5 text-white"
            onClick={() => handleOpen(info.row.original)}
          >
            <IoEye />
          </div>
        );
      },
    }),
  ];

  const handleOpen = async (item) => {
    setViewOpen(item);
    document.body.style.overflow = "hidden";
  };

  const handleClose = () => {
    setViewOpen(null);
    document.body.style.overflow = "unset";
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const options = [
    { label: "All", value: "" },
    { label: "Post", value: "post" },
    { label: "Account", value: "account" },
  ];

  const handleSelectReportType = async (option) => {
    setReportType(option.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const colourStyles = {
    control: (styles) => {
      return {
        ...styles,
        minHeight: "40px",
        height: "40px",
      };
    },
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? "#24388a"
            : "#0b1437",
        color: isDisabled ? "#ccc" : "white",
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? "#24388a"
              : "#1a2d6b"
            : undefined,
        },
        ":hover": {
          backgroundColor: isSelected ? "#24388a" : "#1a2d6b",
          color: "white",
        },
      };
    },
  };

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      <style>{`
        @media (max-width: 767px) {
          .reports-search-container {
            margin-top: 0.75rem !important;
          }
        }
        @media (max-width: 320px) {
          .reports-main-container {
            flex-direction: column !important;
          }
          .reports-search-container {
            width: 100% !important;
          }
          .reports-search-container > div {
            width: 100% !important;
          }
          .reports-search-container input {
            flex: 1 !important;
            width: auto !important;
            max-width: none !important;
          }
          .reports-dropdown-container {
            width: 100% !important;
            margin-top: 0.5rem !important;
            justify-content: flex-start !important;
            align-items: flex-start !important;
          }
          .reports-dropdown-container > div {
            margin-left: 0 !important;
            margin-right: auto !important;
            margin-top: -10px !important;
            margin-bottom: -3px !important;
          }
          .reports-dropdown-container .select__control {
            width: 100% !important;
          }
        }
      `}</style>
      <div className="col-span-1 h-full w-full xl:col-span-1 min-w-0">
        <div className="flex justify-between gap-3 mb-4 reports-main-container">
          <div className="mt-2 md:mt-4 flex h-fit justify-start reports-search-container">
            <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white xl:w-[170px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-[90px] max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="mt-2 flex h-fit w-[280px] justify-end reports-dropdown-container">
            <Select
              className="ms-4 h-full w-[150px] py-1 !text-white"
              classNamePrefix="select"
              defaultValue={options[0]}
              name="color"
              options={options}
              value={options.find((option) => option?.value == reportType)}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isOptionSelected={(option) => option.value == reportType}
              onChange={handleSelectReportType}
              styles={colourStyles}
            />
          </div>
        </div>
        <Card extra={"w-full h-fit pb-6 relative"}>
          <div className="mt-2 mx-2">
            <div className="overflow-x-auto !p-0 !m-0 !pr-0">
              <table className="table-fixed w-full border-collapse !mr-0 !pr-0 !border-r-0" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const columnId = header.column.id;
                      
                      // Set fixed widths for all columns to prevent layout shift
                      let columnWidth = 'auto';
                      if (columnId === 'createdAt') columnWidth = '180px';
                      else if (columnId === 'message') columnWidth = '300px';
                      else if (columnId === 'reporter') columnWidth = '180px';
                      else if (columnId === 'account') columnWidth = '180px';
                      else if (columnId === 'report_type') columnWidth = '150px';
                      else if (columnId === 'action') columnWidth = '100px';
                      
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`cursor-pointer border-b-[1px] border-gray-500/10 pb-2 pt-4 text-start ${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`}
                          style={{ width: columnWidth }}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && tabCache.reports?.firstLoad && !tabCache.reports?.data ? (
                  <tr>
                    <td colSpan={columns.length} className="h-[600px] align-middle">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="h-16 w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-8 align-middle">
                      <div className="flex items-center justify-center">
                        <p className="text-white">No data found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                      return (
                        <tr key={row.id} className={isLastRow ? "" : "border-b-[1px] border-gray-500/10"}>
                          {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFirst = cellIndex === 0;
                            const isLast = cellIndex === row.getVisibleCells().length - 1;
                            const columnId = cell.column.id;
                            
                            // Set fixed widths matching header widths exactly
                            let columnWidth = 'auto';
                            if (columnId === 'createdAt') columnWidth = '180px';
                            else if (columnId === 'message') columnWidth = '300px';
                            else if (columnId === 'reporter') columnWidth = '180px';
                            else if (columnId === 'account') columnWidth = '180px';
                            else if (columnId === 'report_type') columnWidth = '150px';
                            else if (columnId === 'action') columnWidth = '100px';
                            
                            return (
                              <td
                                key={cell.id}
                                className={`border-white/0 py-3 ${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`}
                                style={{ width: columnWidth }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Google-style Pagination */}
          {!isLoading && table.getRowModel().rows.length > 0 && totalPages > 1 && (
            <div className="mt-3 px-6">
              <GooglePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                className="w-full"
              />
            </div>
          )}
        </Card>
      </div>
      {viewOpen && (
        <Details isOpen={viewOpen} data={viewOpen} onClose={handleClose} />
      )}
    </div>
  );
};

export default Reports;
